import React, { useState, useEffect, useCallback } from 'react';
import SmartTable, { SmartTableColumn } from '../../../shared/components/SmartTable';
import SmartTableButtons from '../../../shared/components/SmartTableButtons';
import LayoutWrapper from '../../../shared/components/LayoutWrapper';
import SearchableDropdown from '../../../shared/components/SearchableDropdown';
import { useAuth } from '../../../shared/hooks/useAuth';
import api from '../../../shared/utils/api';
import ErrorPopup from '../../../shared/components/ErrorPopup';
import WarningPopup from '../../../shared/components/WarningPopup';

/** CashflowStatementYear - жилийн мөнгөн гүйлгээний тайлан */
interface CashflowYearRecord {
  Oid: string;
  CustomerOid: string;
  YearType: number;
  Amount: number | null;
  IncomeAmount: number | null;
  OutcomeAmount: number | null;
  CustomerID: string | null;
  CustomerName: string | null;
}

/** CashflowStatementPeriod - сар/үеийн дэлгэрэнгүй */
interface CashflowPeriodRecord {
  YearType: number;
  Amount: number | null;
  Records: number | null;
  CashflowCategoryCode: string | null;
  CashflowCategoryName: string | null;
  CashflowCategorySummaryCode: string | null;
  CustomerID: string | null;
  CustomerName: string | null;
}

interface CustomerOption {
  Oid: string;
  CustomerID: string;
  CustomerName: string;
}

const formatNumber = (value: any) => {
  if (value === null || value === undefined) return '-';
  const num = Number(value);
  if (Number.isNaN(num)) return '-';
  return num.toLocaleString('mn-MN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const CashflowStatement: React.FC = () => {
  const { userAuth } = useAuth();
  const [yearData, setYearData] = useState<CashflowYearRecord[]>([]);
  const [periodData, setPeriodData] = useState<CashflowPeriodRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingPeriod, setLoadingPeriod] = useState(false);
  const [selectedYearRow, setSelectedYearRow] = useState<CashflowYearRecord | null>(null);
  const [showPeriodPopup, setShowPeriodPopup] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showWarningAlert, setShowWarningAlert] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');

  const [selectedYear, setSelectedYear] = useState<string>('2025');
  const [selectedCustomerOid, setSelectedCustomerOid] = useState<string>('');
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [showColumnSearch, setShowColumnSearch] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);

  const fetchCustomers = useCallback(async () => {
    try {
      const accountantOid = userAuth.accountantOid || localStorage.getItem('accountantOid');
      if (!accountantOid) return;
      const queryParams = new URLSearchParams({ accountantOid });
      const result = await api.get(`/api/customerslist?${queryParams.toString()}`);
      if (result?.success && result?.data) {
        setCustomers(result.data.map((c: any) => ({
          Oid: c.Oid,
          CustomerID: c.CustomerID,
          CustomerName: c.CustomerName
        })));
      }
    } catch (error) {
      console.error('❌ Error fetching customers:', error);
    }
  }, [userAuth.accountantOid]);

  const fetchCashflowYear = useCallback(async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      queryParams.set('Year', selectedYear);
      queryParams.set('yearType', selectedYear);
      if (selectedCustomerOid) queryParams.set('customerOid', selectedCustomerOid);

      const result = await api.get(`/api/cashflow-year?${queryParams.toString()}`);
      if (result?.success && result?.data) {
        setYearData(result.data);
        if (result.data.length === 0) {
          setWarningMessage('Мөнгөн гүйлгээний тайлан (жил) олдсонгүй');
          setShowWarningAlert(true);
        } else {
          setShowWarningAlert(false);
        }
      } else {
        setYearData([]);
        setErrorMessage(result?.message || 'Мөнгөн гүйлгээний тайлан авахад алдаа гарлаа');
        setShowErrorPopup(true);
      }
    } catch (error: any) {
      console.error('❌ Error fetching cashflow-year:', error);
      setYearData([]);
      setErrorMessage(error?.response?.data?.message || 'Мөнгөн гүйлгээний тайлан авахад алдаа гарлаа');
      setShowErrorPopup(true);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedCustomerOid]);

  const fetchCashflowPeriod = useCallback(async (cashflowStatementYearOid: string, yearType: number) => {
    try {
      setLoadingPeriod(true);
      setPeriodData([]);
      const queryParams = new URLSearchParams();
      queryParams.set('CashflowStatementYearOid', cashflowStatementYearOid);
      queryParams.set('YearType', String(yearType));
      const url = `/api/cashflow-period?${queryParams.toString()}`;
      const result = await api.get(url);
      if (result?.success && result?.data) {
        setPeriodData(result.data);
      } else {
        setPeriodData([]);
      }
    } catch (error: any) {
      console.error('❌ Error fetching cashflow-period:', error);
      setPeriodData([]);
    } finally {
      setLoadingPeriod(false);
    }
  }, []);

  const handleRowSelect = useCallback((row: CashflowYearRecord) => {
    if (!row?.Oid || row.YearType == null) return;
    setSelectedYearRow(row);
    setShowPeriodPopup(true);
    fetchCashflowPeriod(row.Oid, row.YearType);
  }, [fetchCashflowPeriod]);

  const handleRefresh = () => {
    fetchCashflowYear();
    if (selectedYearRow) {
      fetchCashflowPeriod(selectedYearRow.Oid, selectedYearRow.YearType);
    }
  };

  const renderAmount = (value: any, fallback: string = '-') => {
    const text = value != null ? formatNumber(value) : fallback;
    return <span className="block text-right">{text}</span>;
  };

  const getYearColumns = (): SmartTableColumn[] => [
    { key: 'CustomerID', label: 'Харилцагчийн ID', width: 120, render: (v: any) => v ?? '-' },
    { key: 'CustomerName', label: 'Харилцагчийн нэр', width: 220, render: (v: any) => v ?? '-' },
    { key: 'YearType', label: 'Жил', width: 90, render: (v: any) => v ?? '-' },
    {
      key: 'Amount',
      label: 'Нийт дүн',
      width: 140,
      render: (value: any, row: any, isFooter?: boolean) => renderAmount(value)
    },
    {
      key: 'IncomeAmount',
      label: 'Орлого',
      width: 140,
      render: (value: any) => renderAmount(value)
    },
    {
      key: 'OutcomeAmount',
      label: 'Зарлага',
      width: 140,
      render: (value: any) => renderAmount(value)
    }
  ];

  const getPeriodColumns = (): SmartTableColumn[] => [
    { key: 'YearType', label: 'Жил', width: 70, render: (v: any) => v ?? '-' },
    { key: 'CashflowCategoryCode', label: 'Ангилал код', width: 100, render: (v: any) => v ?? '-' },
    { key: 'CashflowCategoryName', label: 'Мөнгөн гүйлгээний ангилал', width: 380, render: (v: any) => v ?? '-' },
    { key: 'CashflowCategorySummaryCode', label: 'Товч код', width: 85, render: (v: any) => v ?? '-' },
    {
      key: 'Amount',
      label: 'Дүн',
      width: 150,
      render: (value: any) => renderAmount(value)
    },
    {
      key: 'Records',
      label: 'Бичлэг',
      width: 75,
      render: (v: any) => <span className="block text-right">{v ?? '-'}</span>
    }
  ];

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    if (selectedYear) fetchCashflowYear();
  }, [fetchCashflowYear, selectedYear, selectedCustomerOid]);

  const customerOptions = [
    { value: '', label: 'Бүгд' },
    ...customers.map(c => ({ value: c.Oid, label: `${c.CustomerName || ''} (${c.CustomerID || ''})` }))
  ];
  const selectedCustomerLabel = !selectedCustomerOid
    ? 'Бүгд'
    : (customers.find(c => c.Oid === selectedCustomerOid)
        ? `${customers.find(c => c.Oid === selectedCustomerOid)?.CustomerName || ''} (${customers.find(c => c.Oid === selectedCustomerOid)?.CustomerID || ''})`
        : '');

  return (
    <LayoutWrapper userAuth={userAuth}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Мөнгөн гүйлгээний тайлан</h1>
          <SmartTableButtons
            showColumnSearch={showColumnSearch}
            onToggleSearch={() => setShowColumnSearch(s => !s)}
            onColumnSettings={() => setShowColumnSettings(s => !s)}
            onRefresh={handleRefresh}
            addable={false}
          />
        </div>

        <div className="mb-4 flex items-center gap-4">
          <div className="bg-white rounded-lg shadow p-4 min-w-[150px]">
            <div className="text-sm text-gray-600">Нийт бичлэг</div>
            <div className="text-2xl font-bold text-gray-800">{yearData.length}</div>
          </div>
          <div className="flex-1 flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Харилцагч сонгох (Заавал биш)</label>
              <SearchableDropdown
                options={customerOptions}
                value={selectedCustomerOid}
                onChange={setSelectedCustomerOid}
                valueLabel={selectedCustomerLabel}
                placeholder="Бүгд"
                className="w-full"
                enableAutoComplete={true}
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Жил сонгох</label>
              <input
                type="text"
                value={selectedYear}
                onChange={e => setSelectedYear(e.target.value)}
                placeholder="Жил (жишээ: 2025)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            <p className="mt-2 text-gray-600">Уншиж байна...</p>
          </div>
        )}

        {!loading && (
          <>
            <p className="text-sm text-gray-500 mb-2">
              Мөр дээр дарж дэлгэрэнгүй тайлан харах.
            </p>
            <SmartTable
              data={yearData}
              columns={getYearColumns()}
              fetchData={fetchCashflowYear}
              settingsKey="cashflow-year-table"
              editable={false}
              addable={false}
              rowKey="Oid"
              showColumnSearch={showColumnSearch}
              showColumnSettings={showColumnSettings}
              onEdit={handleRowSelect}
            />
          </>
        )}

        {/* Дэлгэрэнгүй тайлан popup */}
        {showPeriodPopup && selectedYearRow && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
            onClick={() => setShowPeriodPopup(false)}
          >
            <div
              className="bg-white rounded-lg shadow-xl p-6 max-w-6xl w-full mx-2 max-h-[90vh] overflow-hidden flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-800">
                  Дэлгэрэнгүй — {selectedYearRow.CustomerName} ({selectedYearRow.YearType})
                </h3>
                <button
                  type="button"
                  onClick={() => setShowPeriodPopup(false)}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none transition-colors p-1"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex-1 overflow-auto min-h-0">
                {loadingPeriod && (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                    <p className="mt-2 text-gray-600">Уншиж байна...</p>
                  </div>
                )}
                {!loadingPeriod && periodData.length > 0 && (
                  <div className="[&_table]:border-spacing-0 [&_td]:!py-0 [&_th]:!py-0 [&_td_span]:!py-0 [&_th_span]:!py-0 [&_td]:leading-none [&_th]:leading-none [&_td_span]:leading-none">
                    <SmartTable
                      data={periodData}
                      columns={getPeriodColumns()}
                      fetchData={() => selectedYearRow && fetchCashflowPeriod(selectedYearRow.Oid, selectedYearRow.YearType)}
                      settingsKey="cashflow-period-table-popup"
                      editable={false}
                      addable={false}
                      rowKey="CashflowCategoryCode"
                      showColumnSearch={false}
                      showColumnSettings={false}
                    />
                  </div>
                )}
                {!loadingPeriod && periodData.length === 0 && (
                  <p className="text-gray-500 py-4">Дэлгэрэнгүй бичлэг олдсонгүй.</p>
                )}
              </div>
            </div>
          </div>
        )}

        <ErrorPopup isVisible={showErrorPopup} message={errorMessage} onClose={() => setShowErrorPopup(false)} />
        <WarningPopup
          isVisible={showWarningAlert}
          message={warningMessage}
          onClose={() => setShowWarningAlert(false)}
          duration={5000}
        />
      </div>
    </LayoutWrapper>
  );
};

export default CashflowStatement;
